// Shared chrome for the public marketing pages (landing, deployment, security, component
// pages): the floating header, the moss footer, design-system buttons and link helpers.
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, BookOpen, Download, Check, Copy, X, Code2, Plus } from 'lucide-react';
import { getVscodeDownloadInfo } from '../../api';
import { isSignedIn, dashboardPath } from '../../auth';
import NodeField from '../../brand/NodeField';
import useBrandTheme from '../../brand/useBrandTheme';
import SocialLinks from '../../brand/SocialLinks';
import './landing.css';

const PUBLIC = process.env.PUBLIC_URL || '';
export const asset = (path) => `${PUBLIC}${path}`;

/* ── Where links go ──────────────────────────────────────────────────────────
   Sections of the home page are addressed as /#id (SitePage scrolls to the hash);
   pages the design has but the app doesn't resolve to the docs or to email. */
export const TO = {
  systemModel: '/#system-model',
  models: '/#models',
  stack: '/#stack',
  compliance: '/#compliance',
  evidence: '/#evidence',
  workflow: '/#workflow',
  deployment: '/deployment',
  security: '/security',
  gateway: '/latentgateway',
  docs: '/docs',
  docsLatentcode: '/docs/latentcode',
  docsAgents: '/docs/agents',
  contact: 'mailto:contact@latentforce.ai?subject=LatentStack%20evaluation',
  evaluation: 'https://www.latentforce.ai/contact',
};

// Web links on other sites open in a new tab.
const external = (to) => /^https?:\/\//.test(to);
const newTab = { target: '_blank', rel: 'noopener noreferrer' };

// A text link: router navigation for app paths, a plain anchor for mailto/tel/external.
export function Go({ to, className, children }) {
  if (to.startsWith('/')) return <Link to={to} className={className}>{children}</Link>;
  return <a href={to} className={className} {...(external(to) ? newTab : {})}>{children}</a>;
}

// Overlay shown when the user clicks "Install": the one-command install (latentcode CLI +
// the LatentWork desktop app) for the visitor's OS, the matching uninstall, and the VS Code
// extension when the server has one. The install/uninstall scripts are served same-origin
// by this server (it injects its own URL), so the commands point at this host. Portalled
// to <body>, outside .lsl, so it carries its own paper-theme styles (.lsm-* in landing.css).
const IS_WINDOWS = typeof navigator !== 'undefined' && /Win/i.test(navigator.platform || navigator.userAgent);

function DownloadModal({ onClose }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const [os, setOs] = useState(IS_WINDOWS ? 'windows' : 'unix');
  const [copied, setCopied] = useState(null);
  const [vscode, setVscode] = useState(null);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let active = true;
    getVscodeDownloadInfo()
      .then((res) => { if (active && res.data?.available) setVscode(res.data); })
      .catch(() => { /* extension row just stays hidden */ });
    return () => { active = false; };
  }, []);

  const copy = (text, id) => {
    navigator.clipboard?.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const cmds = os === 'windows'
    ? { install: `irm ${origin}/install.ps1 | iex`, uninstall: `irm ${origin}/uninstall.ps1 | iex` }
    : { install: `curl -fsSL ${origin}/install.sh | bash`, uninstall: `curl -fsSL ${origin}/uninstall.sh | bash` };

  const Cmd = ({ id }) => (
    <div className="lsm-cmd">
      <span aria-hidden className="lsm-prompt">{os === 'windows' ? 'PS>' : '$'}</span>
      <code>{cmds[id]}</code>
      <button type="button" onClick={() => copy(cmds[id], id)} className="lsm-copy" aria-label="Copy command">
        {copied === id ? <Check size={14} /> : <Copy size={14} />}
        {copied === id ? 'Copied' : 'Copy'}
      </button>
    </div>
  );

  return createPortal(
    <div className="lsm" role="dialog" aria-modal="true" aria-labelledby="lsm-title">
      <div className="lsm-scrim" onClick={onClose} />
      <div className="lsm-card">
        <div className="lsm-head">
          <div className="lsm-head-top">
            <span className="lsm-eyebrow">Install · one command</span>
            <button type="button" onClick={onClose} aria-label="Close" className="lsm-close">
              <X size={16} />
            </button>
          </div>
          <h2 id="lsm-title">Install LatentStack</h2>
          <p>
            Installs <code>latentcode</code>, the terminal CLI, and the LatentWork desktop app, already pointed at
            this gateway.
          </p>
        </div>

        <div className="lsm-body">
          <div role="group" aria-label="Operating system" className="lsm-toggle">
            <button type="button" aria-pressed={os === 'unix'} onClick={() => setOs('unix')}>macOS / Linux</button>
            <button type="button" aria-pressed={os === 'windows'} onClick={() => setOs('windows')}>Windows (PowerShell)</button>
          </div>

          <div className="lsm-label">Run in a terminal</div>
          <Cmd id="install" />

          <details className="lsm-details">
            <summary>
              <span className="lsm-label">Uninstall</span>
              <Plus size={14} aria-hidden />
            </summary>
            <Cmd id="uninstall" />
          </details>

          {vscode && (
            <a href={vscode.url} download className="lsm-ext">
              <span className="lsm-ext-icon"><Code2 size={18} /></span>
              <span className="lsm-ext-text">
                <strong>VS Code extension</strong>
                <span>
                  .vsix{vscode.version ? ` · v${vscode.version}` : ''} — Extensions → ⋯ → Install from VSIX
                </span>
              </span>
              <span className="lsm-ext-cta"><Download size={14} /> Download</span>
            </a>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Footer "Explore" links: only components with a page in the app.
const COMPONENTS = [
  { name: 'LatentCode', to: TO.docsLatentcode },
  { name: 'LatentGateway', to: TO.gateway },
  { name: 'Managed agents', to: TO.docsAgents },
];

const EXTERNAL_LINKS = [
  { label: 'Field notes ↗', href: 'https://blogs.latentforce.ai' },
];


/* ── Buttons ─────────────────────────────────────────────────────────────── */
const ICON_SIZE = { sm: 14, md: 16, lg: 18 };

// Pill button from the LatentForce design system. Renders a router Link for app
// paths, a plain anchor for mailto and external targets (external ones in a new tab),
// and a <button> otherwise.
export function Btn({ to, onClick, variant = 'primary', size = 'md', iconLeft: Left, iconRight: Right, children }) {
  const className = `lsl-btn lsl-btn--${variant} lsl-btn--${size}`;
  const s = ICON_SIZE[size];
  const inner = <>{Left && <Left size={s} aria-hidden />}{children}{Right && <Right size={s} aria-hidden />}</>;
  if (to && to.startsWith('/')) return <Link to={to} className={className}>{inner}</Link>;
  if (to) return <a href={to} className={className} {...(external(to) ? newTab : {})}>{inner}</a>;
  return <button type="button" onClick={onClick} className={className}>{inner}</button>;
}

// "Dashboard" for signed-in visitors, "Sign in" otherwise.
export function PrimaryCta({ size }) {
  return isSignedIn()
    ? <Btn to={dashboardPath()} size={size} iconRight={ArrowRight}>Dashboard</Btn>
    : <Btn to="/login" size={size} iconRight={ArrowRight}>Sign in</Btn>;
}

export function InstallButton({ size }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Btn variant="outline" size={size} iconLeft={Download} onClick={() => setOpen(true)}>Install</Btn>
      {open && <DownloadModal onClose={() => setOpen(false)} />}
    </>
  );
}

/* ── Header ──────────────────────────────────────────────────────────────── */
export function Header() {
  return (
    <header className="lsl-header">
      <div className="lsl-wrap">
        <div className="lsl-header-bar">
          <Link to="/" className="lsl-logo"><img src={asset('/latentstack-logo.svg')} alt="LatentStack" /></Link>
          <div className="lsl-header-actions">
            <SocialLinks />
            <Btn to={TO.docs} variant="ghost" size="sm" iconLeft={BookOpen}>Docs</Btn>
            <PrimaryCta size="sm" />
          </div>
        </div>
      </div>
    </header>
  );
}

/* ── Footer ──────────────────────────────────────────────────────────────── */
export function Footer() {
  return (
    <footer className="lsl-footer">
      <NodeField className="lsl-field">
        <div className="lsl-wrap lsl-footer-grid">
          <div>
            <img src={asset('/latentstack-logo-light.svg')} alt="LatentStack by LatentForce" />
            <div className="lsl-label lsl-footer-by">LatentStack by LatentForce</div>
            <p className="lsl-footer-tagline">Software creation, on your terms.</p>
            <div className="lsl-footer-links lsl-footer-contact">
              <a href="mailto:contact@latentforce.ai">contact@latentforce.ai</a>
              <a href="tel:+918130391497">+91 8130391497</a>
            </div>
            <SocialLinks tone="dark" />
          </div>
          <div>
            <div className="lsl-label">Explore</div>
            <div className="lsl-footer-links">
              <Go to={TO.stack}>LatentStack</Go>
              {COMPONENTS.map((c) => <Go key={c.name} to={c.to} className="lsl-dim">{c.name}</Go>)}
            </div>
          </div>
          <div>
            <div className="lsl-label">Evaluate</div>
            <div className="lsl-footer-links">
              <Go to={TO.deployment}>Deployment</Go>
              <Go to={TO.evidence}>Evidence</Go>
              <Go to={TO.security}>Security</Go>
              <Go to={TO.workflow}>How it works</Go>
              <Link to={TO.docs}>Docs</Link>
              {isSignedIn() ? <Link to={dashboardPath()}>Dashboard</Link> : <Link to="/login">Sign in</Link>}
            </div>
          </div>
          <div>
            <div className="lsl-label">Company</div>
            <div className="lsl-footer-links">
              <Go to={TO.evaluation}>Contact us ↗</Go>
              {EXTERNAL_LINKS.map((l) => (
                <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer">{l.label}</a>
              ))}
            </div>
          </div>
        </div>
        <div className="lsl-wrap lsl-footer-legal">
          <span>© 2026 Latent Force Intelligence Private Limited</span>
        </div>
      </NodeField>
    </footer>
  );
}

/* ── Page shell ──────────────────────────────────────────────────────────── */
// Header + main + footer on the brand paper ground. On navigation it scrolls to the
// linked /#section, or to the top of a new page.
export function SitePage({ rootRef, children }) {
  useBrandTheme({ paperOnly: true });
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (el) {
        el.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return (
    <div ref={rootRef} className="lsl">
      <Header />
      <main style={{ flex: '1 1 auto' }}>{children}</main>
      <Footer />
    </div>
  );
}
